import { OP_LABEL, mergeSpend, spentNothing } from './types'
import type { Op, Session } from './types'
// A runtime edge into the engine, and a safe one: `acts.ts` imports nothing but
// types, so it cannot pull anything back into core's boot path.
import { actsStreamFilter } from '../engine/acts'
import {
  CONJECTURE_HYPOTHESIS,
  CONSENSUS_HYPOTHESIS,
  designOf,
  executedResults,
  isConjectureContract,
  type ExecutableRange,
  type ExecutedResult,
} from './executableConjecture'
import type { ExperimentSpec } from './experiments'

/**
 * What a narration is called wherever an op's name is printed.
 *
 * A narration's receipt is an ordinary op of kind `research` (D91), and
 * `OP_LABEL.research` is "Research analysis" — which is what a usability study
 * found printed over the voice's words: an eyebrow that reads like an
 * authoritative analysis, over the model's own unchecked commentary on the
 * ask channel, which has no search and no gate. The kind stays `research`, so
 * the receipt, the ledger and every codec are untouched; only the NAME
 * follows the `narration` field, and it says what the words are.
 */
export const NARRATION_LABEL = 'Narration · not checked'

/**
 * What a conjecture's executable-test pass is called wherever an op's name is
 * printed. It is filed as a `research` op too (D283), because its product lives
 * in `Session.research`, and so it was named "Research analysis" in the feed, the
 * announcer, the line over the field and the pass-time record — a Lab analysis
 * it is not. The kind stays; the name follows `isExecutableTest` (D289).
 */
export const EXECUTABLE_LABEL = 'Executable test'

/**
 * What the executable-test pass's research contract says it is for. Here rather
 * than in the engine because it is how this module knows the pass afterwards:
 * the engine sends this sentence as the request's purpose, the request's receipt
 * keeps it (`ModelCallReceipt.researchContract`), and `isExecutableTest` reads it
 * back — one sentence, so the one it sends is the one it looks for.
 */
export const EXECUTABLE_PURPOSE =
  'Write one executable test of this conjecture’s registered prediction — two programs, two datasets, one metric and two declared ranges — or none, if no computation could tell it apart from the expectation it departs from.'

/**
 * Executable-test passes pressed in this page and not yet on the record as one.
 *
 * In memory, never stored, and only for the window the record cannot answer:
 * from `beginOp` until the request's receipt lands, the op is a `research` op on
 * a conjecture and nothing else — and a Research Lab portfolio or blind round
 * aimed at a conjecture is exactly that too. That window is the whole of the
 * live line, the start announcement and the "usually" figure, so the engine says
 * which pass it started, once, right after it starts it (`runExecutableTest`).
 */
const pressedHere = new Set<string>()

/** Mark an op as the executable-test pass this page just started. */
export function markExecutableTest(opId: string): void {
  pressedHere.add(opId)
}

type OpIdentity = Pick<Op, 'id' | 'kind' | 'narration' | 'modelCalls'>

/**
 * The Research Lab contract a conjecture's executable-test pass wrote, or null.
 *
 * The join is the contract's id, which `runExecutableTest` makes the op's own —
 * never the node or the registration, so another research pass aimed at the same
 * conjecture can never be read as this one (D283). The workspace's copy first;
 * where somebody has since removed it there, the copy each of its receipts froze.
 * And it must be a conjecture's contract, with the conjecture/consensus pair.
 */
export function executableContractOf(
  session: Pick<Session, 'research'>,
  op: OpIdentity,
): ExperimentSpec | null {
  if (op.kind !== 'research' || op.narration) return null
  const w = session.research?.experiments
  const spec =
    w?.experiments.find((x) => x.id === op.id) ??
    w?.receipts.find((r) => r.experimentId === op.id)?.spec
  return spec && isConjectureContract(spec) ? spec : null
}

/**
 * Whether this op is a conjecture's executable test — the one predicate the
 * feed, the announcer, the line over the field, the pass-time record, the voice
 * and the export read, so no two of them can file the same pass two ways.
 *
 * Three signals, each the record's own where the record has one:
 *
 *  - the contract it wrote (`executableContractOf`), which travels with the
 *    exploration, a shared copy included;
 *  - its request's receipt, whose research contract names this pass's purpose —
 *    the record for a pass that wrote no contract (an empty answer, a proposal
 *    the client refused, a refusal, a failure), though not in a shared copy,
 *    which leaves every receipt behind;
 *  - the press, in this page, for the seconds before either exists.
 */
export function isExecutableTest(session: Pick<Session, 'research'>, op: OpIdentity): boolean {
  if (op.kind !== 'research' || op.narration) return false
  return (
    executableContractOf(session, op) !== null ||
    (op.modelCalls ?? []).some((c) => c.researchContract?.purpose === EXECUTABLE_PURPOSE) ||
    pressedHere.has(op.id)
  )
}

/** An op's printed name: `OP_LABEL` for every pass, `NARRATION_LABEL` for a
 *  narration's receipt and `EXECUTABLE_LABEL` for an executable test. The one
 *  place that choice is made. */
export const opLabel = (op: OpIdentity, session: Pick<Session, 'research'>): string =>
  op.narration
    ? NARRATION_LABEL
    : isExecutableTest(session, op)
      ? EXECUTABLE_LABEL
      : OP_LABEL[op.kind]

/**
 * What an executable-test pass left, read off the record every time and stored
 * nowhere (D283): its contract, the ranges the contract declared, how many times
 * it has run, and each execution's reading.
 *
 * The readings are `executedResults`' — the conjecture card's own derivation, off
 * each receipt, never a copy — narrowed to this contract by id. They need the
 * conjecture the contract names, so where that is gone, or cannot be read as the
 * one the contract tested, there are executions and fewer readings, and a surface
 * says the difference rather than printing a reading it cannot derive.
 */
export interface ExecutableRecord {
  spec: ExperimentSpec
  design: ReturnType<typeof designOf>
  conjectureRange: ExecutableRange
  consensusRange: ExecutableRange
  /** Receipts on record under this contract, whatever they read. */
  executions: number
  /** Their readings, newest first. */
  results: ExecutedResult[]
}

const rangeOf = (spec: ExperimentSpec, prefix: string): ExecutableRange => {
  const h = spec.hypotheses.find((x) => x.id.startsWith(prefix))!
  return { minimum: h.prediction.minimum, maximum: h.prediction.maximum }
}

/**
 * A finished executable-test pass, as a surface reads it: the contract it wrote,
 * or `'none'` for one that left no contract. Null for any other op, and for one
 * still running — what it left is not known until it lands.
 */
export function executableOutcome(
  session: Pick<Session, 'id' | 'nodes' | 'research' | 'ops'>,
  op: OpIdentity & Pick<Op, 'finishedAt' | 'targetIds'>,
): ExecutableRecord | 'none' | null {
  if (op.finishedAt === undefined || !isExecutableTest(session, op)) return null
  const spec = executableContractOf(session, op)
  if (!spec) return 'none'
  const w = session.research?.experiments
  const node = spec.context.nodeId ? session.nodes[spec.context.nodeId] : undefined
  return {
    spec,
    design: designOf(spec, w?.artifacts ?? []),
    conjectureRange: rangeOf(spec, CONJECTURE_HYPOTHESIS),
    consensusRange: rangeOf(spec, CONSENSUS_HYPOTHESIS),
    executions: (w?.receipts ?? []).filter((r) => r.experimentId === spec.id).length,
    results: node ? executedResults(session, node).filter((r) => r.experimentId === spec.id) : [],
  }
}

/**
 * What a finished narration said to the reader, off the RECORD — or null when
 * the record holds nothing that was shown: a narration still speaking, one that
 * failed or was declined, one whose words never survived.
 *
 * The voice turn the reader saw lives in this browser's conversation cache
 * (`core/conversations.ts`), which never travels; the words below are what does.
 * Read from the receipt's output through the SAME filter the live voice ran
 * (`actsStreamFilter`), so the proposal trailer is cut exactly where the reader's
 * turn stopped and the two texts compare equal. A share link carries the words
 * in `narration.shown` instead, because it leaves every receipt behind.
 */
export function narrationWords(
  op: Pick<Op, 'narration' | 'finishedAt' | 'error' | 'refused' | 'modelCalls'>,
): string | null {
  if (!op.narration || op.finishedAt === undefined || op.error || op.refused) return null
  const carried = op.narration.shown
  if (carried !== undefined) return carried.trim() ? carried : null
  const output = op.modelCalls?.[0]?.textContext?.output
  if (!output) return null
  const filter = actsStreamFilter()
  filter.push(output)
  filter.flush()
  const { shown } = filter.result()
  return shown.trim() ? shown : null
}

/** Background narration uses the ordinary operation ledger without taking the
 * reasoning lock. Its receipt and usage land atomically, once, on its payer. */
export function retainNarration(session: Session, operation: Op): Session | null {
  if (operation.kind !== 'research' || !operation.narration || !operation.model) return null
  const previous = session.ops.find((op) => op.id === operation.id)
  if (previous?.finishedAt !== undefined) return session
  if (previous && (!previous.narration || previous.model?.id !== operation.model.id)) return null
  const measured = operation.modelCalls?.[0]?.usage
  const usage = measured && !spentNothing(measured) ? measured : undefined
  const held = { ...operation, ...(usage ? { spend: usage } : {}) }
  return {
    ...session,
    ops: previous
      ? session.ops.map((op) => (op.id === held.id ? held : op))
      : [...session.ops, held],
    spend: usage ? mergeSpend(session.spend, usage) : session.spend,
  }
}
